import React from 'react';
import {GamesFilterStrategyBase} from "../games-filter-strategy-base";
import {AdvancedGamesFilterDrawerRenderer} from "./advanced-games-filter-drawer-renderer";
import {AdvancedStrategyFiltersEditor} from "./advanced-strategy-filters-editor";
import {IArenaGameFiltersCategory} from "../../arena-filters/categories/arena-game-filters-category.interface";
import {ApplyGameFilterResult, IGameFilterEvaluator} from "../../evaluators/game-filter-evaluator.interface";
import {AND} from "../../evaluators/and-game-filters-evaluator";
import {
    FiltersMobileButtonComponent
} from "../../../../components/header/top-header-left/filters/filters-mobile-button.component";
import {Nullable} from "../../../../../common/types/nullable-types";
import {
    IFilterIndicatorRenderingOptions,
    FilterIndicatorRenderingLocation
} from "../../games-filter.service.interface";
import {
    AppliedFiltersIndicatorForAdvancedFilteringComponent
} from "../../../../components/filters/advanced-filtering/indicators/pc-indicator/applied-filters-pc-indicator-for-advanced-filtering.component";
import {DynamicArenaGameFiltersCategory} from "../../arena-filters/categories/dynamic-arena-game-filters-category";
import {Check} from '@sparkware/app-utils';
import {IFilterCategoryDefinition} from '../../filters-categories-definitions/filter-category-definition.interface';
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {AdvancedFiltersAnalytics, IReportApplyFiltersParams} from "./advanced-filters-analytics";
import {GamesMap} from "../../../games/games.service.interface";
import {IAdvancedFiltersEditorToStrategyAdapter} from "./advanced-filters-editor-to-strategy-adapter";

export class AdvancedGamesFilterStrategy extends GamesFilterStrategyBase implements IAdvancedFiltersEditorToStrategyAdapter {

    constructor(services: IServiceFactoryExtended) {
        super(services);
        this._analyticsReporter = new AdvancedFiltersAnalytics(this.services);
    }

    private readonly _analyticsReporter: AdvancedFiltersAnalytics;

    protected _createFiltersCategories(): IArenaGameFiltersCategory[] {
        const arenaName = this.services.routing.currentRoute.data?.arenaType;
        if (!arenaName) {
            return [];
        }

        const categories = this.services.configuration.data.arenas[arenaName]?.advancedFilters?.categories;
        if (!Check.isArray(categories)) {
            return [];
        }
        
        const filters: IArenaGameFiltersCategory[] = [];
        categories.forEach(category => {
            if (category?.id) {
                const filterCategoryDef = this._factory.createCategoryDefinition(category.id);
                if(filterCategoryDef) {
                    filters.push(this._createArenaGameFiltersCategory(filterCategoryDef));
                } else {
                    this.services.logger.error(`There is no filter category definition for id ${category.id}`);
                }
            }
        });

        return filters;
    }

    protected _createArenaGameFiltersCategory(filterCategoryDef: IFilterCategoryDefinition): IArenaGameFiltersCategory {
        return new DynamicArenaGameFiltersCategory(filterCategoryDef, this.services);
    }

    protected _createEvaluator(): IGameFilterEvaluator | null {
        return AND(...this.filtersCategories);
    }

    private _createFiltersEditor() {
        return new AdvancedStrategyFiltersEditor(this, this.services);
    }

    getApplyFilterReportingParameters(): IReportApplyFiltersParams {
        const appliedFilters: Record<string, string[]> = {};
        this.filtersCategories.forEach(category => {
            const appliedFiltersInCategory = category.allAppliedFiltersAndSubFilters;
            if(appliedFiltersInCategory.length > 0) {
                appliedFilters[category.id] = appliedFiltersInCategory.map(f => f.id);
            }
        })
        const gamesCount = this.services.arenas.getCurrentArena().filteredGamesCount;
        return {
            gamesCount: gamesCount,
            appliedFilters: appliedFilters
        };
    }

    protected _reportApplyFiltersByDeepLink(): void {

        this._analyticsReporter.reportApplyFiltersByDeepLink(this.getApplyFilterReportingParameters());

    }

    renderMobileFiltersIndicator(): React.ReactElement {
        if(this.services.mediaQuery.lAndBelow) {
            return (<FiltersMobileButtonComponent/>);
        } else {
            return null;
        }
    }

    renderFiltersIndicator(): Nullable<IFilterIndicatorRenderingOptions> {
        if(this.services.mediaQuery.lAndBelow) {
            return {
                location: FilterIndicatorRenderingLocation.BelowArenaTitle,
                reactElement: (<AppliedFiltersIndicatorForAdvancedFilteringComponent showFiltersButton={false} filtersEditor={this._createFiltersEditor()}/>)
            }
        } else {
            return {
                location: FilterIndicatorRenderingLocation.NextToArenaTitle,
                reactElement: (<AppliedFiltersIndicatorForAdvancedFilteringComponent filtersEditor={this._createFiltersEditor()}/>)
            };
        }
    }

    async showFiltersEditor(): Promise<void> {
        (await this.services.drawer).right.push(new AdvancedGamesFilterDrawerRenderer(this.services, this._createFiltersEditor()));
    }

    getNumberOfGamesBeforeUserApply(filterIDS: string[]): number {
        let gamesToShowIfUserApply = this._filterGamesBeforeApplyClicked(filterIDS);
        return Object.keys(gamesToShowIfUserApply).length;
    }

    private _filterGamesBeforeApplyClicked(filterIDS: string[]): GamesMap {
        const currentArenaGames = this.services.arenas.getCurrentArena().allArenaGamesForFiltering;

        if(!this.currentFilters.some(f => filterIDS.includes(f.id))) {
            return currentArenaGames;
        }

        const evaluator = this._createEvaluator();

        const result: GamesMap = {};

        for(let game of Object.values(currentArenaGames)) {
            const filterResult = evaluator.applyFilter(game, filterIDS);
            if(ApplyGameFilterResult.IncludeGame === filterResult) {
                result[game.gameType] = game;
            }
        }

        return result;
    }

}